import { ApolloLink, Observable } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { CLIENT_UPDATE_REQUIRED, graphQLErrorCode } from '@hopdrive/feature-move-planner/request';

import { CLIENT_IDENTITY } from '../clientVersion';

/** The header that tells the server which dealer build sent a request. */
export const CLIENT_HEADER = 'x-hopdrive-client';

/**
 * The headers every dealer GraphQL request carries besides the token: the
 * Hasura client name and the build identity. The HTTP link, the WebSocket
 * connect and the plain-fetch helper all take them from here. With no identity
 * the header is left out, never sent as "undefined".
 */
export function clientHeaders(clientName: string): Record<string, string> {
  return {
    'hasura-client-name': clientName,
    ...(CLIENT_IDENTITY ? { [CLIENT_HEADER]: CLIENT_IDENTITY } : {}),
  };
}

interface HeaderDeps {
  getToken: () => Promise<string | undefined>;
  clientName: string;
}

/** Adds the token and the client headers to every HTTP operation. */
export function createAuthLink({ getToken, clientName }: HeaderDeps): ApolloLink {
  return setContext(async (_, { headers }) => ({
    headers: {
      ...headers,
      authorization: `Bearer ${await getToken()}`,
      ...clientHeaders(clientName),
    },
  }));
}

/**
 * The WebSocket's `connectionParams`. It runs on every (re)connect, so a
 * reconnect always picks up a fresh token.
 */
export function wsConnectionParams({ getToken, clientName }: HeaderDeps) {
  return async () => ({
    headers: {
      authorization: `Bearer ${await getToken()}`,
      ...clientHeaders(clientName),
    },
  });
}

interface ErrorDeps {
  /** Forces a token refresh. The retry reads the new token through the auth link, so any return value is ignored. */
  refreshToken: () => Promise<unknown>;
  /** The session can't be refreshed: send the user to log in. */
  onAuthFailure: () => void;
  /** The server refused this build (`CLIENT_UPDATE_REQUIRED`): hand the page to the app updater. */
  onUpdateRequired?: () => void;
}

/**
 * Retries an operation once with a fresh token when Hasura says the JWT
 * expired, and sends the user to log in when the refresh fails or the server
 * answers 401. The retry goes back through the auth link, so it carries the
 * client headers like any other request.
 */
export function createErrorLink({ refreshToken, onAuthFailure, onUpdateRequired }: ErrorDeps): ApolloLink {
  return onError(({ graphQLErrors, networkError, operation, forward }) => {
    // Once per response, whichever operation it was. The error still reaches
    // the caller, so a screen can keep its form and skip its own toast; the
    // updater's action ignores a second call if the screen also makes one.
    if (onUpdateRequired && graphQLErrors?.some(err => graphQLErrorCode(err) === CLIENT_UPDATE_REQUIRED)) {
      onUpdateRequired();
    }

    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        if (err.extensions?.code === 'UNAUTHENTICATED' || err.message.includes('JWTExpired')) {
          // Return an Observable that handles the async token refresh
          return new Observable(observer => {
            refreshToken()
              .then(() => {
                // The retry goes back through the auth link, which sets the
                // refreshed token; this link leaves authorization alone.
                // Subscribe to the retried operation and forward results to the observer
                const subscriber = {
                  next: observer.next.bind(observer),
                  error: observer.error.bind(observer),
                  complete: observer.complete.bind(observer),
                };
                forward(operation).subscribe(subscriber);
              })
              .catch(error => {
                console.error('Error refreshing token:', error);
                onAuthFailure();
                observer.error(error);
              });
          });
        }
      }
    }

    if (networkError) {
      console.error(`[Network Error]: ${networkError.message}`);
    }

    if (networkError && 'statusCode' in networkError && networkError.statusCode === 401) {
      onAuthFailure();
    }
  });
}
